import { useEffect, useState } from 'react'
import { ProductAccountDTO, AssetDTO } from 'api-models'
import { useStores } from 'hooks'
import { getPermissionTier } from 'utils'
import { features } from 'config/features'
import { OnboardingInterstitialChoice } from '../onboarding-interstitial-choice'
import { VerificationController } from 'components/inactive-tracking-code/verification-controller'
import { useTrackingState } from './hooks/use-tracking-state'
import { TrackingTabs } from './components/tracking-tabs'
import { ServerSideTab } from './components/server-side-tab/server-side-tab'
import { TrackingCodeTab } from './components/tracking-code-tab/tracking-code-tab'
import { GtmInstall } from './components/tracking-code-tab/gtm-install'
import { GButton } from 'components/basic-blocks'
import { AdAccountLinks } from 'services/user/ad-account-links'

const BackArrowIcon = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		className={className}
	>
		<path
			d="M7.25592 1.42259C7.58136 1.09715 7.58136 0.569515 7.25592 0.244078C6.93049 -0.0813592 6.40285 -0.0813592 6.07741 0.244078L0.244078 6.07741C-0.0813592 6.40285 -0.0813592 6.93049 0.244078 7.25592L6.07741 13.0893C6.40285 13.4147 6.93049 13.4147 7.25592 13.0893C7.58136 12.7638 7.58136 12.2362 7.25592 11.9107L2.84518 7.5L12.5 7.5C12.9602 7.5 13.3333 7.1269 13.3333 6.66667C13.3333 6.20643 12.9602 5.83333 12.5 5.83333L2.84518 5.83333L7.25592 1.42259Z"
			style={{ fill: 'var(--t-desc-gray)' }}
		/>
	</svg>
)

interface InstallTrackingCodeProps {
	account: ProductAccountDTO
	asset: AssetDTO
	onSuccess?: () => Promise<void>
	onScheduleCall?: () => void
	showInterstitial?: boolean
	helpPanelOpen?: boolean
}

export const hasFeature = (
	account: ProductAccountDTO,
	feature: string
): boolean => {
	const selectedTier = account.billing?.subscription?.next_plan?.tier
	const tier = getPermissionTier(
		selectedTier || account.billing?.subscription?.plan?.tier
	)
	if (!tier) return false
	if (!features[feature]) return false
	if (tier === 'legacy') return !!features[feature]['standard']

	return !!features[feature][tier]
}

export const InstallTrackingCode = ({
	account,
	asset,
	onSuccess,
	onScheduleCall,
	showInterstitial = false,
	helpPanelOpen = false
}: InstallTrackingCodeProps) => {
	const { accountStore, assetStore } = useStores()

	const [hasDismissedInterstitial, setHasDismissedInterstitial] =
		useState(false)
	const showSetupChoice = showInterstitial && !hasDismissedInterstitial
	const [showVerificationModal, setShowVerificationModal] = useState(false)
	const [showGtmInstall, setShowGtmInstall] = useState(false)

	const hasServerSideTracking = hasFeature(account, 'server_side_tracking')

	const {
		activeTab,
		setActiveTab,
		serverSideState,
		trackingCodeState,
		selectedMethod,
		setSelectedMethod,
		resetServerSide,
		skipServerSide,
		completeServerSide,
		completeTrackingCode
	} = useTrackingState(asset)

	useEffect(() => {
		accountStore.setAccount(account)
		assetStore.setAsset(asset)
		assetStore.refreshAdAccounts()
	}, [account.id, asset.id])

	// Remember a Shopify connection — default the method to 'shopify' when the asset is already linked.
	useEffect(() => {
		let cancelled = false
		AdAccountLinks.getShopifyLink(account.id, asset.id)
			.then((link) => {
				if (!cancelled && link) setSelectedMethod('shopify')
			})
			.catch(() => undefined)
		return () => {
			cancelled = true
		}
	}, [account.id, asset.id])

	const handleStartVerification = () => {
		setShowVerificationModal(true)
	}

	const handleVerificationClose = () => {
		// Just dismiss the modal (backdrop click, X button, etc.)
		setShowVerificationModal(false)
	}

	const handleGoToDashboard = async () => {
		// Explicitly navigate to dashboard (from "Go to dashboard" button)
		if (onSuccess) {
			await onSuccess()
		}
		setShowVerificationModal(false)
	}

	const handleDirectComplete = async () => {
		// Complete setup directly without verification modal
		// Used by GTM (success or not) and Email Developer ("Go to dashboard")
		if (onSuccess) {
			await onSuccess()
		}
	}

	const handleGtmInstall = () => {
		setShowGtmInstall(true)
	}
	const handleVerify = handleStartVerification

	const handleTabChange = (tab: 'server-side' | 'tracking-code') => {
		if (tab === 'server-side') {
			resetServerSide()
		} else {
			setActiveTab(tab)
		}
	}

	const handleInterstitialDismiss = () => {
		setHasDismissedInterstitial(true)
	}

	if (showSetupChoice) {
		return (
			<div
				className="bg-white rounded-[24px] shadow-lg md:p-12 p-6"
				style={{ width: '620px', maxWidth: '100%' }}
			>
				<OnboardingInterstitialChoice
					account={account}
					asset={asset}
					onScheduleCall={onScheduleCall}
					onDismiss={handleInterstitialDismiss}
				/>
			</div>
		)
	}

	return (
		<>
			<VerificationController
				open={showVerificationModal}
				onClose={handleVerificationClose}
				onGoToDashboard={handleGoToDashboard}
				accountId={account.id}
				assetId={asset.id}
			/>

			<div
				className="bg-white rounded-[24px] md:p-[48px] p-[24px]"
				style={{ width: '620px', maxWidth: '100%' }}
			>
				<div
					className={`flex flex-col w-full ${
						showGtmInstall ? 'gap-6' : 'gap-4'
					}`}
				>
					{(showGtmInstall ||
						(!showGtmInstall &&
							hasServerSideTracking &&
							activeTab === 'tracking-code')) && (
						<button
							className="flex items-center gap-2 text-[16px] font-medium text-t-desc-gray hover:text-t-default transition-colors self-start"
							onClick={() => {
								if (showGtmInstall) {
									setShowGtmInstall(false)
								} else {
									resetServerSide()
								}
							}}
						>
							<BackArrowIcon />
							Back
						</button>
					)}
					<h1 className="md:text-[30px] text-[24px] md:leading-[36px] leading-[28px] font-medium text-t-default">
						Install your Tracking Code
					</h1>

					{showGtmInstall ? (
						<GtmInstall onComplete={handleDirectComplete} />
					) : (
						<>
							{hasServerSideTracking ? (
								<div className="flex flex-col gap-6">
									<TrackingTabs
										activeTab={activeTab}
										serverSideState={serverSideState}
										trackingCodeState={trackingCodeState}
										onTabChange={handleTabChange}
									/>

									{activeTab === 'server-side' && (
										<ServerSideTab
											onSkip={skipServerSide}
											onComplete={completeServerSide}
										/>
									)}

									{activeTab === 'tracking-code' && (
										<TrackingCodeTab
											accountId={account.id}
											assetId={asset.id}
											selectedMethod={selectedMethod}
											onGtmInstall={handleGtmInstall}
											onMethodChange={setSelectedMethod}
											onVerify={handleVerify}
											onDirectComplete={handleDirectComplete}
										/>
									)}
								</div>
							) : (
								<TrackingCodeTab
									accountId={account.id}
									assetId={asset.id}
									selectedMethod={selectedMethod}
									onGtmInstall={handleGtmInstall}
									onMethodChange={setSelectedMethod}
									onVerify={handleVerify}
									onDirectComplete={handleDirectComplete}
								/>
							)}
						</>
					)}
				</div>
			</div>
		</>
	)
}
